// The chrome around whichever module is active (spec, The header: 5; D-046).
//
// The platform's own chrome — the header (logo, module switcher, theme toggle, user and
// sign-out) and the platform bands — renders OUTSIDE the module's Provider, so it stays
// whatever the Provider does. The Provider wraps only the module's own pieces: its two
// header slots, its BelowHeader row and the routed page. A Provider that renders a loading
// or error gate instead of its children (Inventory's did, 2026-10-16, and blanked the
// switcher and sign-out) now loses only those pieces.
//
// The header slots need the Provider's context (Support's drafts, LaunchPad's nav counts)
// but must appear in the header, so they render inside the Provider and are PORTALLED into
// two targets in the header. Context and React events follow the React tree, not the DOM,
// so the slots read the Provider as before; their DOM sits where it always did.
//
// THE TARGETS. Each is a `display: contents` <span> in the header whose ref callback is a
// state setter. The first commit mounts the header with empty targets; the ref callbacks
// run in that commit's layout phase and their state updates re-render synchronously,
// before the browser paints, so the slots appear in the first painted frame: no flash and
// no jump. The setters are stable, so the targets are set once per mount and the slots
// never remount on a re-render (a fresh inline callback would detach and re-attach on
// every render, and remount every slot with it). A shell-created DOM node would skip the
// second render but would mount the slots into a detached node first; the ref is simpler.
import { Fragment, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { useSession, useSignOut } from './session/SessionProvider.jsx';
import PlatformHeader from './PlatformHeader.jsx';
import SlotBoundary from './SlotBoundary.jsx';

/**
 * The module's header slots, portalled into the header's targets. Rendered inside the
 * module's Provider, so each slot reads its context. Each keeps its SlotBoundary: a crash
 * hides the slot, not the header. A slot waits for its target (null until the header's
 * first commit). `portal` is createPortal; tests pass a stand-in, since the server renderer
 * they use cannot render portals.
 */
export function ModuleSlots({ entry, targets, portal = createPortal }) {
  const NavExtras = entry?.HeaderNavExtras;
  const Actions = entry?.HeaderActions;
  return (
    <>
      {NavExtras && targets.navExtras
        && portal(<SlotBoundary name="HeaderNavExtras"><NavExtras /></SlotBoundary>, targets.navExtras)}
      {Actions && targets.actions
        && portal(<SlotBoundary name="HeaderActions"><Actions /></SlotBoundary>, targets.actions)}
    </>
  );
}

export default function PlatformLayout({ active, available, entry, children }) {
  const user = useSession();
  const signOut = useSignOut();
  const [signOutError, setSignOutError] = useState(null);
  const [signingOut, setSigningOut] = useState(false);
  // The header's slot targets (see THE TARGETS above). useState setters are stable.
  const [navExtras, setNavExtras] = useState(null);
  const [actions, setActions] = useState(null);

  useEffect(() => {
    document.title = active ? `Adora Ops – ${active.label}` : 'Adora Ops';
  }, [active]);

  // A sign-out that FAILED must not look like one that worked (unchanged from Support).
  async function handleSignOut() {
    setSigningOut(true);
    setSignOutError(null);
    try {
      await signOut();
    } catch (error) {
      setSigningOut(false);
      setSignOutError(error?.message ?? 'Sign-out failed. Reload the page and try again.');
    }
  }

  // A Provider that THROWS still reaches Shell.jsx's root boundary: a boundary here would
  // have to show something in the page's place, and that is a separate decision.
  const Provider = entry?.Provider ?? Fragment;
  const BelowHeader = entry?.BelowHeader;

  return (
    <div className="min-h-full">
      <PlatformHeader active={active} available={available} entry={entry}
        onSignOut={handleSignOut} signingOut={signingOut}
        navExtrasTarget={setNavExtras} actionsTarget={setActions} />
      {/* A sign-out that FAILED must not look like one that worked — the whole point of
          the control is a shared desk, and "I clicked it, so I'm out" is the assumption it
          has to earn. A stale CSRF token is the realistic failure (403), and it is
          recoverable: reload, GET /api/me mints a fresh one, try again. */}
      {signOutError && (
        <div role="alert" className="border-b border-rose-200 bg-rose-50 px-4 py-1.5 text-center text-xs text-rose-900
                     dark:border-rose-500 dark:bg-rose-900 dark:text-rose-100">
          You are still signed in. {signOutError}
        </div>
      )}
      {/* Local dev runs with a stub identity; make that impossible to mistake for
          real auth. `auth_is_stub` is false whenever SSO is the authentication in
          play — src/routes/auth.js sets it explicitly false at the callback — and the
          server refuses to boot with a stub identity outside development
          (`assertStubAuthAllowed`, src/server.js).

          The one hand-rolled dark treatment in this file. A warning band has
          to keep shouting on a dark page, where a subtle tint reads as chrome, so it
          takes a filled amber-900 with a brighter top border rather than the faint
          amber-50 wash that is enough against white. It stays distinct from the
          internal-note amber (SPEC.md §7.4, `bg-note`), which is darker and browner —
          these are the only two warm surfaces in the app and they must not be
          confusable with each other either. */}
      {user.auth_is_stub && (
        <div className="border-b px-4 py-1.5 text-center text-xs
                     bg-amber-50 border-amber-200 text-amber-900
                     dark:bg-amber-900 dark:border-amber-500 dark:text-amber-100">
          Development mode, signed in as <strong>{user.email}</strong> (stub user).
          This is not Entra ID SSO: no password was checked and any browser on this
          machine is this user.
        </div>
      )}
      {/* The module's own pieces, and only they, inside its Provider. BelowHeader now
          follows the platform bands rather than preceding them: the bands are platform
          chrome and stay outside the Provider, and the page must follow BelowHeader. */}
      <Provider>
        <ModuleSlots entry={entry} targets={{ navExtras, actions }} />
        {BelowHeader && <SlotBoundary name="BelowHeader"><BelowHeader /></SlotBoundary>}
        {children}
      </Provider>
    </div>
  );
}
